// ============================================
// Garment Bar — 6-slot horizontal garment strip
// Shows the nurse's current outfit as 6 context
// categories with icons, labels, and intensity.
// ============================================

import { T, F } from '../../quality/tokens';
import type { GarmentState, GarmentPiece } from '../../../types/fashionPlate';

// Garment piece icons (emoji for now, SVG later)
const GARMENT_ICONS: Record<GarmentPiece, string> = {
  shoes: '👟',
  heart: '❤️',
  brain: '🧠',
  hat: '🎓',
  dress: '👗',
  accessories: '👓',
};

const GARMENT_LABELS: Record<GarmentPiece, string> = {
  shoes: 'Pace',
  heart: 'Empathy',
  brain: 'Depth',
  hat: 'Authority',
  dress: 'Tone',
  accessories: 'Modifiers',
};

const INTENSITY_COLORS: Record<string, string> = {
  minimal: T.textGhost,
  moderate: T.warning,
  full: T.success,
  max: T.accent,
};

interface GarmentBarProps {
  garments: GarmentState[];
  highlightPiece?: GarmentPiece | null;
  onPieceClick?: (piece: GarmentPiece) => void;
  compact?: boolean;
}

export function GarmentBar({ garments, highlightPiece, onPieceClick, compact }: GarmentBarProps) {
  const pieces: GarmentPiece[] = ['shoes', 'heart', 'brain', 'hat', 'dress', 'accessories'];

  return (
    <div style={{
      display: 'flex', gap: compact ? 4 : 8,
      padding: compact ? '8px 12px' : '12px 16px',
      background: T.bgCard,
      borderRadius: T.radius.lg,
      border: `1px solid ${T.border}`,
      boxShadow: T.shadowSm,
      overflowX: 'auto',
    }}>
      {pieces.map((piece) => {
        const garment = garments.find(g => g.piece === piece);
        const isDressed = !!garment;
        const isHighlighted = highlightPiece === piece;
        const intensity = garment?.intensity ?? 'minimal';
        const color = isDressed ? INTENSITY_COLORS[intensity] : T.bgInput;

        return (
          <button
            key={piece}
            onClick={() => onPieceClick?.(piece)}
            style={{
              flex: 1, minWidth: compact ? 44 : 52,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: compact ? 2 : 4,
              padding: compact ? '6px 2px' : '8px 4px',
              background: isHighlighted
                ? `${T.accent}12`
                : isDressed ? `${color}10` : 'transparent',
              border: isHighlighted
                ? `2px solid ${T.accent}`
                : isDressed ? `1px solid ${color}30` : `1px solid ${T.borderSoft}`,
              borderRadius: T.radius.md,
              cursor: onPieceClick ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
            }}
          >
            <span style={{
              fontSize: compact ? 16 : 20,
              filter: isDressed ? 'none' : 'grayscale(1) opacity(0.3)',
              transition: 'filter 0.3s ease',
            }}>
              {GARMENT_ICONS[piece]}
            </span>
            <span style={{
              fontSize: compact ? 8 : 9,
              fontFamily: F.mono,
              fontWeight: 600,
              letterSpacing: 0.5,
              color: isDressed ? color : T.textGhost,
              textTransform: 'uppercase',
            }}>
              {GARMENT_LABELS[piece]}
            </span>
            {!compact && garment && (
              <span style={{
                fontSize: 8,
                fontFamily: F.body,
                color: T.textMuted,
                textAlign: 'center',
                lineHeight: 1.2,
                maxWidth: 56,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {garment.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
